import type { AltClickCycleStep } from '../types/alt-click-cycle-step'

/**
 * Picks the target of an Alt+Click.
 *
 * With fewer than two shapes selected, each Alt+Click at a stack moves one object down it, topmost
 * to bottommost, then wraps back to the topmost. Given `memberTopFirst` (inside an isolated group),
 * the first Alt+Click still picks the topmost member at the point clicked, and each later one moves
 * on to the next member of the group in layer order, wherever it sits, then wraps (WMPCM-13465).
 *
 * With a multi-selection the selection never changes. The first Alt+Click highlights the topmost
 * selected object at the point clicked (WMPCM-1660); each later one moves the highlight to the next
 * selected object in layer order, top to bottom, wherever it sits, then wraps.
 *
 * A cycle continues only while the current target is the one the last Alt+Click picked. Anything
 * else (a plain click, a marquee, Shift+Alt+Click) starts again from the point clicked.
 */
export function resolveAltClickCycleStep({
  stackIds,
  selectedTopFirst,
  highlightedId,
  lastCycledId,
  memberTopFirst,
}: {
  /** Selectable shapes under the pointer, topmost first. */
  stackIds: readonly string[]
  /** The selected shapes in layer order, topmost first. */
  selectedTopFirst: readonly string[]
  highlightedId: string | null
  /** The shape the previous Alt+Click picked, if any. */
  lastCycledId: string | null
  /** Every member of the isolated group in layer order, topmost first; absent outside isolation. */
  memberTopFirst?: readonly string[]
}): AltClickCycleStep | null {
  if (selectedTopFirst.length >= 2) {
    const highlightIndex =
      highlightedId != null && highlightedId === lastCycledId
        ? selectedTopFirst.indexOf(highlightedId)
        : -1

    if (highlightIndex >= 0) {
      return {
        kind: 'highlight',
        shapeId: selectedTopFirst[(highlightIndex + 1) % selectedTopFirst.length],
        isContinuing: true,
      }
    }

    const selectedUnderPointer = stackIds.find((id) => selectedTopFirst.includes(id))

    if (selectedUnderPointer) {
      return { kind: 'highlight', shapeId: selectedUnderPointer, isContinuing: false }
    }
  }

  if (stackIds.length === 0) return null

  const cycleIds = memberTopFirst ?? stackIds
  const currentId = selectedTopFirst.length === 1 ? selectedTopFirst[0] : null
  const currentIndex =
    currentId != null && currentId === lastCycledId ? cycleIds.indexOf(currentId) : -1
  const isContinuing = currentIndex >= 0

  return {
    kind: 'select',
    shapeId: isContinuing ? cycleIds[(currentIndex + 1) % cycleIds.length] : stackIds[0],
    isContinuing,
  }
}
